import { useEffect, useRef, useState } from 'react';

import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { markFeatureDisabled } from '../_helpers/features.js';

/*
 * On-demand lyrics for one song (ENG-415), from `GET /songs/{id}/lyrics`
 * (ENG-414). The backend fetches them from Musixmatch at the moment they are
 * asked for and deliberately stores nothing, so there is no lyrics field on
 * any song payload to read instead: the words only exist in the answer to
 * this call, and neither does the attribution the licence requires with them.
 *
 * Two pages ask. The streamer page (`StreamerDashboard`) is the one the issue
 * names: the performer's stand shows what the band saved for a song, and asks
 * here for the words when they saved none. The watch page's sing-along panel
 * asks for the song on stage while a show is live. Both render through the
 * same obligations (`useLyricsImpression`, `LyricsAttribution`).
 *
 * ## The contract
 *
 * Marked as in `_helpers/notifications.js`:
 *   [reconciled, ENG-574] — read off the shipped handler (backend
 *                           `songs/lyrics.go`, `LyricsResponse` and its
 *                           `Code*` constants), `openapi.yaml`
 *                           (`catalogueGetSongLyrics`,
 *                           `CatalogueLyricsResponse`) and the "On-demand
 *                           lyrics" table of `api-error-envelopes.md`; a
 *                           change here has to be a change there too.
 * `_helpers/__fixtures__/songLyrics.json` is one response per state in the
 * shape that handler serializes, taken from its struct and its tests.
 *
 *   GET /songs/{id}/lyrics                                   [reconciled, ENG-574]
 *     authenticated (bearer); a signed-out caller gets the 401 `unauthorized`
 *     envelope, which fetchWrapper does not turn into a logout on this route.
 *     → 200 LyricsResponse, flat, snake_case, `omitempty` on everything but
 *       the first three:
 *         song_id: number, source: 'catalogue' | 'musixmatch', lyrics: string,
 *         language?, provider?, copyright?, backlink_url?, track_url?,
 *         script_tracking_url?, pixel_tracking_url?,
 *         partial?: boolean, restricted?: boolean, message?: string
 *       `catalogue` is the band's own `songs.lyrics` text and carries only
 *       the first three. `musixmatch` carries the attribution; `track_url` is
 *       the song row's share link and stands in for a blank `backlink_url`.
 *       `partial` marks the plan's excerpt (the provider's notice is left in
 *       `lyrics`). `restricted: true` is a withdrawal: `lyrics` is empty,
 *       `message` is prose to show, copyright and backlink stay, and the
 *       tracking URLs are dropped. `provider` is always "Musixmatch" and is
 *       not read: the credit is fixed brand copy (`LyricsAttribution`).
 *     → ENG-384 envelope otherwise, `{error, message, field?, params?}`,
 *       whose `error` fetchWrapper surfaces as `error.code`:
 *         400 invalid_song_id · 401 unauthorized · 404 song_not_found,
 *         song_lyrics_not_found, song_is_instrumental,
 *         song_lyrics_source_unknown · 409 feature_disabled (ENG-781:
 *         the operator's lyrics flag is off and the caller is not a member
 *         or moderator of a band that has the song; params.feature
 *         "lyrics") · 429 song_lyrics_rate_limited ·
 *         503 song_lyrics_unavailable, song_lyrics_not_configured,
 *         song_lyrics_budget_exhausted · 500 internal_error
 *       There is no error code for a withdrawal; it is only ever the 200.
 *     → from the middleware in front of the handler, same envelope:
 *         429 rate_limited (ratelimit.Middleware, the edge limiter on
 *         requests, params.retry_after_seconds) · 401 session_revoked,
 *         login_denied (auth/middleware.go, params.reason) · 500
 *         internal_error. Every 401 is a refusal of the session whatever its
 *         code, and is never remembered as the song's answer.
 *     → nothing in the backend answers 403 on this route (ENG-595); one can
 *       only come from a gateway in front of it, so any 403 reads as
 *       `forbidden`, whatever it carries, and is neither retried nor
 *       remembered.
 *
 * `source` decides whether the display obligations apply, and it is read
 * strictly: only 'musixmatch' is the provider and only 'catalogue' is the
 * band's own text. A value the handler adds later is not taken for either
 * unless the payload carries something only the provider sends (see
 * `isProviderAnswer`), because a Musixmatch credit printed over a band's own
 * words is a false licensing claim, and a missing credit is a defect.
 *
 * The failure codes are distinct on purpose — "no lyrics", "instrumental",
 * "nowhere to look" and "come back later" are four different things to say —
 * so they are passed through to the caller rather than flattened into one.
 * A code the handler adds later is passed through untouched as well and the
 * panel phrases it as "come back later", so it degrades to a sentence rather
 * than to a blank.
 *
 * ## One lookup per song, not one per viewer per song
 *
 * Every read is a metered provider call, and a thousand people watching the
 * same show ask for the same words within the same 10-second queue poll. The
 * frontend cannot deduplicate across viewers — that is the backend's to do —
 * but it can stop *one* browser spending more than one lookup on a song:
 * answers are remembered for the page session, concurrent asks for the same
 * id share one request, and a set that comes back round to an earlier song
 * re-reads nothing. Only settled answers are kept; "come back later" is not
 * an answer, so it is retried instead (`RETRY_DELAYS_MS`).
 */


/** What the panel is showing: one of these, never a bare boolean pair. */
export const LyricsStatus = Object.freeze({
  /** No song on stage, or lyrics not wanted here: render nothing at all. */
  IDLE: 'idle',
  LOADING: 'loading',
  /** A body to show, in `lyrics.body` (with whatever attribution came with it). */
  READY: 'ready',
  /** The rights holder has withdrawn them; `lyrics.restrictionMessage` says so. */
  RESTRICTED: 'restricted',
  /** Nothing to show, for the reason in `code`. */
  UNAVAILABLE: 'unavailable',
});

const IDLE = Object.freeze({ status: LyricsStatus.IDLE, lyrics: null, code: null });
const LOADING = Object.freeze({ status: LyricsStatus.LOADING, lyrics: null, code: null });
const unavailable = (code) => ({ status: LyricsStatus.UNAVAILABLE, lyrics: null, code });

/**
 * Failures that say "not now" rather than "not ever". These are worth asking
 * again for, on a schedule; everything else — no lyrics, instrumental, no
 * source, no session, a spent daily budget, a deployment with no API key —
 * answers the same way however many times it is asked.
 */
const TRANSIENT_CODES = new Set([
  'song_lyrics_rate_limited',
  // The edge limiter's 429 (ratelimit.Middleware), in front of the handler:
  // this browser asked too often, not this song has no words.
  'rate_limited',
  'song_lyrics_unavailable',
  'internal_error',
]);

/**
 * When to ask again after a transient failure, and how often: twice, backing
 * off, then stop. A song is three minutes long, so a third wait would land
 * after the words were any use; and a listener who is being rate-limited is
 * not helped by this browser adding to it.
 */
const RETRY_DELAYS_MS = Object.freeze([3000, 9000]);

/** The 401 codes the panel phrases on their own (auth/middleware.go). */
const SESSION_REFUSED_CODES = new Set(['unauthorized', 'session_revoked', 'login_denied']);

/**
 * What any 403 reads as. Not a backend code: the backend never answers 403
 * here, so this names the one thing the panel knows about such a refusal.
 */
export const FORBIDDEN_CODE = 'forbidden';

/**
 * The envelope's `error` code, or the closest one the status line supports.
 * The handler answers every refusal with the envelope; a gateway answering in
 * its place (a bare 502/503, a 401 with no body) still gets a code the panel
 * can phrase.
 */
const failureCode = (error) => {
  // Any 401 is a refused session, never retried or remembered, so one with a
  // code the panel has no sentence for reads as `unauthorized` rather than
  // as "come back later" (ENG-591).
  if (error?.status === 401 && !SESSION_REFUSED_CODES.has(error?.code)) return 'unauthorized';
  // Nothing in front of this route answers 403 (ENG-595: no role or
  // eligibility guard, and the auth middleware's refusals are 401s), so a 403
  // is a gateway turning the request away. The session is fine, so it is not
  // "sign in"; and it is never retried, so it is not "come back later".
  if (error?.status === 403) return FORBIDDEN_CODE;
  if (typeof error?.code === 'string' && error.code) return error.code;
  switch (error?.status) {
    case 401:
      return 'unauthorized';
    case 404:
      return 'song_lyrics_not_found';
    case 429:
      return 'song_lyrics_rate_limited';
    case 503:
      return 'song_lyrics_unavailable';
    default:
      return 'internal_error';
  }
};

/** The first of `values` that is a non-blank string, else null. */
const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
};

/**
 * The `LyricsResponse` as the panel sees it, read field for field off the
 * handler's JSON tags (see the contract block). Every field but the first
 * three is `omitempty`, so absent is the ordinary case and reads as null or
 * false, never as an error.
 */
export const normalizeLyrics = (input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  // The band's own text — the lyrics a band saved, and every performer
  // original's (ENG-793/794), which is never looked up on Musixmatch — owes
  // no credit and counts no impression. Anything provider-shaped riding on
  // such a payload is dropped here, so no caller can print a Musixmatch
  // credit over a band's own words or load a tracking script for them.
  if (sourceName(input) === CATALOGUE_SOURCE) {
    return {
      songId: input.song_id ?? null,
      source: CATALOGUE_SOURCE,
      body: firstText(input.lyrics),
      language: firstText(input.language),
      copyright: null,
      backlinkUrl: null,
      trackingScriptUrl: null,
      trackingPixelUrl: null,
      restricted: false,
      restrictionMessage: null,
      partial: false,
    };
  }
  return {
    songId: input.song_id ?? null,
    source: firstText(input.source),
    body: firstText(input.lyrics),
    language: firstText(input.language),
    copyright: firstText(input.copyright),
    // `track_url` is the song row's share link (ENG-350), sent as a fallback
    // for a blank `backlink_url`; it points at the same track page.
    backlinkUrl: firstText(input.backlink_url, input.track_url),
    trackingScriptUrl: firstText(input.script_tracking_url),
    trackingPixelUrl: firstText(input.pixel_tracking_url),
    restricted: input.restricted === true,
    restrictionMessage: firstText(input.message),
    partial: input.partial === true,
  };
};

/** The handler's `LyricsSourceMusixmatch`. */
const PROVIDER_SOURCE = 'musixmatch';

/** The handler's `LyricsSourceCatalogue`: the band's own `songs.lyrics` text. */
const CATALOGUE_SOURCE = 'catalogue';

const sourceName = (lyrics) =>
  typeof lyrics?.source === 'string' ? lyrics.source.trim().toLowerCase() : '';

/**
 * Whether these words came from the licensed provider, and so carry the
 * display obligations (credit, brand, impression) with them.
 *
 * `source` answers it: 'musixmatch' is the provider and 'catalogue' is not.
 * Anything else — a value the handler adds later, or a payload with no
 * source — is answered from evidence and not from the absence of evidence: a
 * payload carrying something only the provider sends (a copyright line, a
 * backlink, a tracking URL, a withdrawal) is the provider's, and one with
 * none of those is the band's own text.
 *
 * The failure directions are not symmetric. Missing a provider answer omits
 * a credit that was owed, which is a defect in one panel; claiming one that
 * is not owed prints "Lyrics licensed and provided by Musixmatch" over words
 * a band typed themselves, which is a false statement about someone else's
 * licence. So a `source` this file does not recognise is not read as
 * provider unless the payload backs it up.
 */
export const isProviderAnswer = (lyrics, { restricted = false } = {}) => {
  const name = sourceName(lyrics);
  if (name === CATALOGUE_SOURCE) return false;
  if (name === PROVIDER_SOURCE) return true;
  return Boolean(
    restricted ||
      lyrics?.restricted ||
      lyrics?.copyright ||
      lyrics?.backlinkUrl ||
      lyrics?.trackingScriptUrl ||
      lyrics?.trackingPixelUrl
  );
};

/**
 * What one answer from the endpoint means. A withdrawal is only ever this
 * 200 with `restricted: true`; the handler has no error code for it.
 */
const stateFromBody = (data) => {
  const lyrics = normalizeLyrics(data);
  if (lyrics?.restricted) return { status: LyricsStatus.RESTRICTED, lyrics, code: null };
  // A 200 with nothing in it is the same news as a 404 here, and a reader is
  // owed the same sentence for both.
  if (!lyrics?.body) return unavailable('song_lyrics_not_found');
  return { status: LyricsStatus.READY, lyrics, code: null };
};

/** What one refusal from the endpoint means. */
const stateFromError = (error) => unavailable(failureCode(error));

/**
 * The operator's lyrics flag is off (ENG-781). Neither the song's answer nor
 * "come back later": the hook goes IDLE (renders nothing), does not retry,
 * does not remember it, and turns the flag off for the page so every listener
 * lyrics surface hides (ENG-783). Read off the code, whatever the status.
 */
export const FEATURE_DISABLED_CODE = 'feature_disabled';

const isTransient = (state) =>
  state.status === LyricsStatus.UNAVAILABLE && TRANSIENT_CODES.has(state.code);

/*
 * Answers already paid for, for the life of the page. Module scope rather
 * than a ref: the watch page can unmount this panel and remount it later
 * (between shows, say), and a set that comes back round to a song played earlier
 * should cost nothing either time. Bounded because a long show is a long
 * queue, and evicted oldest-first.
 *
 * A refused session is never in here — `unauthorized`, or any 401 whatever
 * its code (`session_revoked`, `login_denied`): it is about the session or
 * the account, which can change while the page is open (signing in again, an
 * account re-enabled), not the song's answer — and neither is anything else
 * worth asking again for.
 */
const CACHE_LIMIT = 32;
const answered = new Map();
const inFlight = new Map();

const remember = (key, state) => {
  if (answered.size >= CACHE_LIMIT) answered.delete(answered.keys().next().value);
  answered.set(key, state);
};

/** Reset between tests; the page itself never needs this. */
export const clearSongLyricsCache = () => {
  answered.clear();
  inFlight.clear();
};

/**
 * One request per song id, however many callers ask at once. Settled answers
 * are kept; "come back later" is not settled, so it leaves nothing behind
 * for the retry to find.
 */
const loadLyrics = (fetchWrapper, songId) => {
  const key = String(songId);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = fetchWrapper
    .get(`${config.apiBaseUrl}/songs/${encodeURIComponent(songId)}/lyrics`)
    .then(
      (data) => ({ state: stateFromBody(data), sessionRefused: false }),
      (error) => {
        if (error?.code === FEATURE_DISABLED_CODE) {
          markFeatureDisabled('lyrics');
          return { state: IDLE, sessionRefused: false, featureDisabled: true };
        }
        const state = stateFromError(error);
        // A refusal of the session (401) or by a gateway (403) is about this
        // browser, not the song, and can lift while the page is open, so
        // neither is remembered (ENG-595).
        const refused = error?.status === 401 || error?.status === 403 || state.code === 'unauthorized';
        return { state, sessionRefused: refused };
      }
    )
    .then(({ state, sessionRefused, featureDisabled }) => {
      inFlight.delete(key);
      if (!isTransient(state) && !sessionRefused && !featureDisabled) remember(key, state);
      return state;
    });

  inFlight.set(key, request);
  return request;
};

/**
 * Lyrics for `songId`, refetched whenever the song on stage changes and
 * cleared back to IDLE when there is none. `enabled` is how a caller says
 * "not now" (the show is offline) without breaking the rules of hooks.
 *
 * The request is made whether or not this browser holds a session, and the
 * backend decides. The route is authenticated, so a signed-out listener on
 * the public watch page gets the `unauthorized` envelope — one cheap 401 per
 * song, which the panel phrases as "sign in" — and a token this browser still
 * holds but the backend no longer accepts is refused the same way.
 * `/songs/{id}/lyrics` is exempt from fetchWrapper's auto-logout so that
 * refusal cannot eject a listener from the stream (see fetchWrapper).
 */
export const useSongLyrics = (songId, { enabled = true } = {}) => {
  const [state, setState] = useState(IDLE);
  const fetchWrapperRef = useRef(useFetchWrapper());

  useEffect(() => {
    if (!enabled || songId == null || songId === '') {
      setState(IDLE);
      return undefined;
    }

    const key = String(songId);
    const cached = answered.get(key);
    if (cached) {
      // Already paid for this song on this page; no flash of "Finding the
      // words…" for words we already have.
      setState(cached);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let retries = 0;
    setState(LOADING);

    const run = () => {
      loadLyrics(fetchWrapperRef.current, songId).then((next) => {
        if (cancelled) return;
        setState(next);
        // The sentence stays on screen while this happens: a silent second
        // try is better than a spinner, and better than a dead panel for the
        // rest of the song.
        if (isTransient(next) && retries < RETRY_DELAYS_MS.length) {
          timer = setTimeout(run, RETRY_DELAYS_MS[retries]);
          retries += 1;
        }
      });
    };

    run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [songId, enabled]);

  return state;
};

export default useSongLyrics;
